import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput, fmtDate, panelPost, useAdminGet,
} from './kit';
import type { AdminTargetIn } from './adminNav';
import SpendPanel, { usd } from './SpendPanel';

/**
 * /admin → AWS & API spend (Backend/admin_aws.py, section "aws"; API spend is
 * Backend/api_spend.py, section "spend").
 *
 *  - Overview: this month so far and the forecast, last month, credits AWS
 *    applied, what was paid, the AWS credit left (API spend → Credits), usage
 *    per month, cost by project, open health issues.
 *  - Invoices: every AWS invoice with its credits, tax and total, and the PDF.
 *  - Costs: usage by service per month, daily usage, last month's and this
 *    month's bill by usage type.
 *  - Resources: everything in the account with configuration, health, metrics
 *    and cost (last month, this month, run-rate), by project; the project can
 *    be set by hand, with a note.
 *  - Health: what needs attention (status checks, metrics, alarms, idle spend).
 *  - API spend: Replicate, Horizon, members, manual costs, credits, keys.
 *
 * Deep links: /admin?group=aws&tab=<overview|invoices|costs|resources|health|api_spend>.
 */

const S = 'aws';
const TABS = ['Overview', 'Invoices', 'Costs', 'Resources', 'Health', 'API spend'];
const TAB_PARAM: Record<string, string> = {
  overview: 'Overview', invoices: 'Invoices', costs: 'Costs', resources: 'Resources', health: 'Health',
  api_spend: 'API spend', spend: 'API spend',
};
const PROJECT_ORDER = ['UniversityTech', 'R&D List', 'OCEAN', 'Other'];
const LEVEL: Record<string, { label: string; cls: string }> = {
  ok: { label: 'OK', cls: 'bg-green-50 text-green-700 border-green-200' },
  idle: { label: 'Idle', cls: 'bg-grayscale-200 text-grayscale-1200 border-grayscale-300' },
  warn: { label: 'Check', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  bad: { label: 'Problem', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const int = (v: any) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US'));
const monthLabel = (m: string) => {
  const d = new Date(`${m}-01T00:00:00`);
  return isNaN(d.getTime()) ? m : d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};
const ago = (iso?: string | null) => {
  if (!iso) return 'not read yet';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(m)) return fmtDate(iso);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return fmtDate(iso);
};

const Badge: React.FC<{ level: string; title?: string }> = ({ level, title }) => {
  const l = LEVEL[level] || LEVEL.ok;
  return <span title={title} className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${l.cls}`}>{l.label}</span>;
};

// ---------------------------------------------------------------------------
// Snapshot status + refresh (shared by every tab)
// ---------------------------------------------------------------------------
const NAMES: Record<string, string> = { resources: 'Resources', costs: 'Costs', invoices: 'Invoices' };

const SnapshotBar: React.FC<{ snapshots: any; onChange: () => void }> = ({ snapshots, onChange }) => {
  const [err, setErr] = useState('');
  const running = Object.values(snapshots || {}).some((s: any) => s?.job?.running);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(onChange, 6000);
    return () => clearInterval(t);
  }, [running, onChange]);
  const go = async (what: string) => {
    setErr('');
    const { error } = await panelPost(S, 'refresh', { what });
    if (error) setErr(error);
    onChange();
  };
  const errors: string[] = Object.values(snapshots || {}).flatMap((s: any) => [...(s?.errors || []), ...((s?.job && !s.job.running && s.job.errors) || [])]);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
        {Object.entries(snapshots || {}).map(([k, s]: [string, any]) => (
          <span key={k}>
            {NAMES[k] || k} {s?.job?.running ? <b className="text-primary-700">reading…</b> : <>read {ago(s?.taken_at)}</>}
          </span>
        )).reduce((acc: React.ReactNode[], el, i) => (i ? [...acc, <span key={`s${i}`}>·</span>, el] : [el]), [])}
        <Btn small onClick={() => go('all')} disabled={running}>{running ? 'Reading from AWS…' : 'Refresh from AWS'}</Btn>
      </div>
      {(err || errors.length > 0) && (
        <div className="text-xs text-amber-700">{err || Array.from(new Set(errors)).slice(0, 4).join(' · ')}</div>
      )}
    </div>
  );
};

const Waiting: React.FC<{ what: string }> = ({ what }) => (
  <div className="text-sm text-grayscale-1100 py-6 text-center">Reading {what} from AWS for the first time — this takes about a minute.</div>
);

// ---------------------------------------------------------------------------
// Monthly bars (one series: usage per month)
// ---------------------------------------------------------------------------
const MonthBars: React.FC<{ months: any[] }> = ({ months }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...months.map(m => m.usage || 0));
  const shown = hover !== null ? months[hover] : null;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-grayscale-1100 min-h-[1rem]">
        {shown ? <><b className="text-grayscale-1300">{usd(shown.usage)}</b> used in {monthLabel(shown.month)} · credits {usd(shown.credits)} · paid {usd(shown.net)}</>
          : <>AWS usage per month, before credits</>}
      </div>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-14 shrink-0 h-36">
          <span>{usd(max)}</span><span>$0</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative h-36 border-b border-grayscale-300" onMouseLeave={() => setHover(null)}>
            <div className="absolute inset-0 flex items-end gap-1">
              {months.map((m, i) => (
                <div key={m.month} className="flex-1 h-full flex items-end" onMouseEnter={() => setHover(i)}>
                  <div className={`w-full rounded-t ${hover === i ? 'bg-primary-700' : 'bg-primary-500'}`}
                    style={{ height: m.usage ? `max(2px, ${(m.usage / max) * 100}%)` : 0 }} />
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-1 text-[10px] text-grayscale-1100 mt-1">
            {months.map(m => <span key={m.month} className="flex-1 text-center truncate">{monthLabel(m.month).replace(' 20', " '")}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const Overview: React.FC<{ tick: number; bump: () => void; open: (t: string) => void }> = ({ tick, bump, open }) => {
  const r = useAdminGet(S, 'overview', {}, [tick]);
  const d: any = r.data;
  const cur = d?.this_month, prev = d?.last_month, cr = d?.credit;
  const months: any[] = (d?.months || []).filter(Boolean);
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {d && <SnapshotBar snapshots={d.snapshots} onChange={bump} />}
      {!d && r.loading ? <Loading /> : d && !months.length && !d.resource_count ? <Waiting what="the account" /> : d && (
        <>
          <KPIRow>
            <KPI label="This month so far" value={usd(cur?.usage)}
              hint={d.forecast_month_usage != null ? `forecast ${usd(d.forecast_month_usage)} for the month` : 'AWS usage, before credits'} />
            <KPI label="Last month" value={usd(prev?.usage)} hint={prev ? `${monthLabel(prev.month)} · paid ${usd(prev.net)}` : ''} />
            <KPI label="Credits applied" value={usd(prev?.credits)} hint={`last month · ${usd(d.months_credits_total)} over ${months.length} months`} />
            <KPI label="AWS credit left" value={cr ? usd(cr.balance) : '—'}
              hint={cr ? (cr.days_left != null ? `about ${cr.days_left > 730 ? '2+ years' : `${int(cr.days_left)} days`} at ${usd(cr.avg_daily * 30)}/month` : '')
                : 'add the credit in API spend → Credits'}
              onClick={cr ? undefined : () => open('API spend')} />
          </KPIRow>
          <Section title="Spending per month">
            <MonthBars months={months} />
            <DataTable csvName="aws_monthly" rows={months.slice().reverse()} defaultSort="-month"
              columns={['month', 'usage', 'credits', 'tax', 'net']}
              labels={{ month: 'Month', usage: 'Usage', credits: 'Credits applied', tax: 'Tax', net: 'Paid' }}
              render={{ month: x => monthLabel(x.month), usage: x => <b>{usd(x.usage)}</b>, credits: x => usd(x.credits),
                tax: x => usd(x.tax), net: x => usd(x.net) }} />
          </Section>
          <Section title="Cost by project" right={<Btn small onClick={() => open('Resources')}>All resources</Btn>}>
            <DataTable csvName="aws_projects" rows={d.projects || []} defaultSort="-last_month"
              columns={['project', 'last_month', 'mtd', 'resources']}
              labels={{ project: 'Project', last_month: 'Last month', mtd: 'This month so far', resources: 'Resources' }}
              render={{ last_month: x => <b>{usd(x.last_month)}</b>, mtd: x => usd(x.mtd), resources: x => (x.resources ? int(x.resources) : '—') }} />
          </Section>
          <Section title="Health" right={<Btn small onClick={() => open('Health')}>Details</Btn>}>
            <div className="flex gap-2 flex-wrap text-sm">
              {['bad', 'warn', 'idle', 'ok'].map(l => (
                <span key={l} className="inline-flex items-center gap-1.5"><Badge level={l} /> {int(d.health?.[l] || 0)}</span>
              ))}
            </div>
            {(d.top_issues || []).length > 0 && (
              <ul className="text-sm text-grayscale-1200 flex flex-col gap-1">
                {d.top_issues.map((i: any) => (
                  <li key={i.id} className="flex gap-2 items-start"><Badge level={i.level} />
                    <span><b>{i.name}</b> <span className="text-grayscale-1100">({i.kind})</span> — {i.reasons.join('; ')}</span></li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------
const Invoices: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'invoices', {}, [tick]);
  const d: any = r.data;
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const pdf = async (id: string) => {
    setBusy(id); setErr('');
    const { data, error } = await panelPost(S, 'invoice_pdf', { invoice_id: id });
    setBusy('');
    if (error) setErr(error); else if (data?.url) window.open(data.url, '_blank', 'noopener');
  };
  const rows: any[] = d?.invoices || [];
  return (
    <div className="flex flex-col gap-4">
      {(r.error || err) && <Msg kind="error">{r.error || err}</Msg>}
      {d && <SnapshotBar snapshots={d.snapshots} onChange={bump} />}
      {!d && r.loading ? <Loading /> : d && !d.taken_at ? <Waiting what="invoices" /> : d && (
        <Section title={`Invoices · account ${d.account || ''}`}>
          <DataTable csvName="aws_invoices" rows={rows} defaultSort="-period" searchable
            columns={['period', 'invoice_id', 'entity', 'type', 'issued', 'subtotal', 'credits', 'tax', 'total', 'payment_total']}
            optionalColumns={['due', 'currency', 'payment_currency', 'original_invoice']}
            labels={{ period: 'Month', invoice_id: 'Invoice', entity: 'Issued by', type: 'Type', issued: 'Issued', subtotal: 'Charges',
              credits: 'Credits', tax: 'Tax', total: 'Total', payment_total: 'Paid', due: 'Due', currency: 'Currency',
              payment_currency: 'Paid in', original_invoice: 'Corrects' }}
            render={{
              period: x => monthLabel(x.period), subtotal: x => usd(x.subtotal), credits: x => usd(x.credits), tax: x => usd(x.tax),
              total: x => <b>{usd(x.total)}</b>,
              payment_total: x => (x.payment_total == null ? '—' : `${x.payment_currency === 'INR' ? '₹' : ''}${Number(x.payment_total).toLocaleString('en-IN')}${x.payment_currency && x.payment_currency !== 'INR' ? ` ${x.payment_currency}` : ''}`),
              type: x => (x.type === 'CREDIT_MEMO' ? 'Credit note' : 'Invoice'),
              issued: x => fmtDate(x.issued), due: x => fmtDate(x.due),
            }}
            actions={x => <Btn small onClick={() => pdf(x.invoice_id)} disabled={busy === x.invoice_id}>{busy === x.invoice_id ? 'Opening…' : 'PDF'}</Btn>}
            empty="No invoices returned by AWS." />
          <div className="text-xs text-grayscale-1100">
            AWS issues two documents a month for the same charges (a tax invoice, numbered AIN…, and an invoice), so add up months from Overview, not from this list.
          </div>
        </Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------
const Costs: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'costs', {}, [tick]);
  const d: any = r.data;
  const [period, setPeriod] = useState('last');
  const months: any[] = d?.by_service || [];
  const lastM = months.length > 1 ? months[months.length - 2] : null;
  const curM = months.length ? months[months.length - 1] : null;
  const services = useMemo(() => (d?.service_totals || []).map((s: any) => ({
    ...s, last_month: lastM?.services?.[s.service] ?? 0, mtd: curM?.services?.[s.service] ?? 0,
  })), [d, lastM, curM]);
  const daily = useMemo(() => (d?.daily || []).map((x: any) => ({
    day: x.day, total: Object.values(x.services || {}).reduce((a: number, b: any) => a + Number(b || 0), 0),
    top: Object.entries(x.services || {}).sort((a: any, b: any) => b[1] - a[1]).slice(0, 4),
  })), [d]);
  const [hover, setHover] = useState<number | null>(null);
  const dmax = Math.max(0.01, ...daily.map((x: any) => x.total));
  const ut = period === 'last' ? d?.usage_types_last_month : d?.usage_types_mtd;
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {d && <SnapshotBar snapshots={d.snapshots} onChange={bump} />}
      {!d && r.loading ? <Loading /> : d && !d.taken_at ? <Waiting what="costs" /> : d && (
        <>
          <Section title="Daily usage, last 60 days">
            <div className="text-xs text-grayscale-1100 min-h-[2rem]">
              {hover !== null && daily[hover] ? (
                <><b className="text-grayscale-1300">{usd(daily[hover].total)}</b> on {daily[hover].day} — {daily[hover].top.map(([k, v]: any) => `${k.replace(/^Amazon |^AWS /, '')} ${usd(v)}`).join(' · ')}</>
              ) : <>Before credits. Hover a day for its biggest services.</>}
            </div>
            <div className="relative h-32 border-b border-grayscale-300" onMouseLeave={() => setHover(null)}>
              <div className="absolute inset-0 flex items-end gap-[2px]">
                {daily.map((x: any, i: number) => (
                  <div key={x.day} className="flex-1 h-full flex items-end" onMouseEnter={() => setHover(i)}>
                    <div className={`w-full rounded-t ${hover === i ? 'bg-primary-700' : 'bg-primary-500'}`}
                      style={{ height: x.total ? `max(2px, ${(x.total / dmax) * 100}%)` : 0 }} />
                  </div>
                ))}
              </div>
            </div>
          </Section>
          <Section title="By service">
            <DataTable csvName="aws_services" rows={services} defaultSort="-last_month"
              columns={['service', 'last_month', 'mtd', 'usd']}
              labels={{ service: 'Service', last_month: lastM ? monthLabel(lastM.month) : 'Last month',
                mtd: curM ? `${monthLabel(curM.month)} so far` : 'This month', usd: `Last ${months.length} months` }}
              render={{ last_month: x => <b>{usd(x.last_month)}</b>, mtd: x => usd(x.mtd), usd: x => usd(x.usd) }} />
          </Section>
          <Section title="The bill line by line" right={
            <Select value={period} onChange={setPeriod} options={['last', 'mtd']} labels={{ last: 'Last month', mtd: 'This month so far' }} />}>
            <DataTable csvName={`aws_usage_types_${period}`} rows={ut || []} defaultSort="-usd" searchable
              columns={['service', 'usage_type', 'usd', 'quantity']}
              labels={{ service: 'Service', usage_type: 'Usage type', usd: 'Cost', quantity: 'Quantity' }}
              render={{ usd: x => <b>{usd(x.usd)}</b>, quantity: x => Number(x.quantity).toLocaleString('en-US', { maximumFractionDigits: 2 }) }} />
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
const fmtVal = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === 'object' ? Object.entries(x).filter(([, y]) => y != null && y !== '').map(([k, y]) => `${k}: ${y}`).join(', ') : String(x))).join(' | ') : '—';
  if (typeof v === 'object') return Object.entries(v).map(([k, y]) => `${k}=${y}`).join(', ') || '—';
  if (typeof v === 'string' && /^\d{4}-\d\d-\d\dT/.test(v)) return fmtDate(v);
  return String(v);
};
const METRIC_LABELS: Record<string, string> = {
  cpu_avg: 'CPU avg 24 h %', cpu_max: 'CPU max 24 h %', connections: 'DB connections (avg)', free_mem: 'Free memory',
  acu: 'Serverless capacity (avg ACU)', mem_avg: 'Memory avg 24 h %', requests: 'Requests 24 h', '5xx': 'Server errors 24 h',
};

const ResourceDetail: React.FC<{ r: any; projects: string[]; onSaved: () => void; onClose: () => void }> = ({ r, projects, onSaved, onClose }) => {
  const [project, setProject] = useState(r.project_set_by_hand ? r.project : '');
  const [note, setNote] = useState(r.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    const { error } = await panelPost(S, 'resource_note', { resource_id: r.id, project: project || null, note });
    setBusy(false);
    if (error) setErr(error); else onSaved();
  };
  const metrics = Object.entries(r.metrics || {});
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary-300 bg-primary-50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-grayscale-1300">{r.name} <span className="font-normal text-grayscale-1100">· {r.kind} · {r.region}</span></div>
          <div className="text-[11px] text-grayscale-1100 break-all">{r.id}</div>
        </div>
        <Btn small onClick={onClose}>Close</Btn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {Object.entries(r.config || {}).map(([k, v]) => (
          <div key={k} className="flex gap-2 min-w-0"><span className="text-grayscale-1100 shrink-0 w-40">{k}</span>
            <span className="text-grayscale-1300 break-words min-w-0">{fmtVal(v)}</span></div>
        ))}
        {metrics.map(([k, v]) => (
          <div key={k} className="flex gap-2"><span className="text-grayscale-1100 shrink-0 w-40">{METRIC_LABELS[k] || k}</span>
            <span className="text-grayscale-1300">{k === 'free_mem' ? `${(Number(v) / 1e9).toFixed(1)} GB` : int(v)}</span></div>
        ))}
        {r.checks && <div className="flex gap-2"><span className="text-grayscale-1100 shrink-0 w-40">status checks</span>
          <span className="text-grayscale-1300">system {r.checks.system || '—'} · instance {r.checks.instance || '—'}</span></div>}
      </div>
      {(r.health?.reasons || []).length > 0 && (
        <div className="text-xs text-grayscale-1200"><Badge level={r.health.level} /> {r.health.reasons.join('; ')}</div>
      )}
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs text-grayscale-1100">Project</span>
        <Select value={project} onChange={setProject} options={['', ...projects]} labels={{ '': `Automatic (${r.project})` }} />
        <TextInput value={note} onChange={setNote} wide placeholder="Note, e.g. what it is for, who owns it, when it can go" />
        <Btn kind="primary" small onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

const Resources: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'resources', {}, [tick]);
  const d: any = r.data;
  const [project, setProject] = useState('');
  const [kind, setKind] = useState('');
  const [open, setOpen] = useState<any>(null);
  const all: any[] = d?.resources || [];
  const rows = all.filter(x => (!project || x.project === project) && (!kind || x.kind === kind))
    .map(x => ({ ...x, health_level: x.health?.level }));
  const projects = Array.from(new Set([...PROJECT_ORDER.filter(p => all.some(x => x.project === p)), ...all.map(x => x.project)]));
  const totals = (p: string) => all.filter(x => x.project === p).reduce((a, x) => a + (x.cost_last_month || 0), 0);
  const sharedLast: number = Object.values(d?.shared_last_month || {}).reduce((a: number, b: any) => a + Number(b || 0), 0) as number;
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {d && <SnapshotBar snapshots={d.snapshots} onChange={bump} />}
      {!d && r.loading ? <Loading /> : d && !d.resources_at ? <Waiting what="resources" /> : d && (
        <>
          <KPIRow>
            {projects.map(p => (
              <KPI key={p} label={p} value={usd(totals(p))} hint={`last month · ${all.filter(x => x.project === p).length} resources`}
                onClick={() => setProject(project === p ? '' : p)} active={project === p} />
            ))}
            {sharedLast > 0 && <KPI label="Shared" value={usd(sharedLast)} hint="not tied to one resource" />}
          </KPIRow>
          {open && <ResourceDetail key={open.id} r={open} projects={d.projects || PROJECT_ORDER}
            onSaved={() => { setOpen(null); bump(); }} onClose={() => setOpen(null)} />}
          <Section title={`Resources · account ${d.account || ''}`} right={
            <Select value={kind} onChange={setKind} options={['', ...Object.keys(d.kinds || {}).sort()]}
              labels={{ '': 'Every kind', ...Object.fromEntries(Object.entries(d.kinds || {}).map(([k, n]) => [k, `${k} (${n})`])) }} />}>
            <DataTable csvName="aws_resources" rows={rows} defaultSort="-cost_last_month" searchable maxHeight="40rem"
              columns={['name', 'kind', 'project', 'health_level', 'state', 'cost_last_month', 'cost_mtd', 'cost_run_rate', 'region']}
              optionalColumns={['note', 'id']}
              labels={{ name: 'Name', kind: 'Kind', project: 'Project', health_level: 'Health', state: 'State',
                cost_last_month: 'Last month', cost_mtd: 'This month', cost_run_rate: 'Run-rate', region: 'Region', note: 'Note', id: 'Id' }}
              render={{
                name: x => <button type="button" className="text-left text-primary-700 underline underline-offset-2" onClick={() => setOpen(x)}>{x.name}</button>,
                project: x => <span title={x.project_set_by_hand ? 'set by hand' : 'automatic'}>{x.project}{x.project_set_by_hand ? ' ✎' : ''}</span>,
                health_level: x => <Badge level={x.health_level} title={(x.health?.reasons || []).join('; ')} />,
                cost_last_month: x => <b>{usd(x.cost_last_month)}</b>, cost_mtd: x => usd(x.cost_mtd), cost_run_rate: x => usd(x.cost_run_rate),
              }}
              actions={x => <Btn small onClick={() => setOpen(x)}>Details</Btn>}
              empty="No resources match." />
            {sharedLast > 0 && (
              <div className="text-xs text-grayscale-1100">
                Shared last month: {Object.entries(d.shared_last_month || {}).sort((a: any, b: any) => b[1] - a[1]).map(([k, v]) => `${k} ${usd(v)}`).join(' · ')}
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------
const Health: React.FC<{ tick: number; bump: () => void }> = ({ tick, bump }) => {
  const r = useAdminGet(S, 'health', {}, [tick]);
  const d: any = r.data;
  return (
    <div className="flex flex-col gap-4">
      {r.error && <Msg kind="error">{r.error}</Msg>}
      {!d && r.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="Problems" value={int(d.counts?.bad || 0)} />
            <KPI label="To check" value={int(d.counts?.warn || 0)} />
            <KPI label="Idle" value={int(d.counts?.idle || 0)} hint="running but unused, or billed while unused" />
            <KPI label="OK" value={int(d.counts?.ok || 0)} hint={d.resources_at ? `checked ${ago(d.resources_at)}` : ''} />
          </KPIRow>
          <Section title="Needs attention" right={<Btn small onClick={async () => { await panelPost(S, 'refresh', { what: 'resources' }); bump(); }}>Check again</Btn>}>
            <DataTable csvName="aws_health" rows={(d.issues || []).map((i: any) => ({ ...i, why: i.reasons.join('; ') }))}
              columns={['level', 'name', 'kind', 'project', 'why', 'cost_last_month', 'region']}
              labels={{ level: 'Level', name: 'Name', kind: 'Kind', project: 'Project', why: 'Why', cost_last_month: 'Last month', region: 'Region' }}
              render={{ level: x => <Badge level={x.level} />, cost_last_month: x => usd(x.cost_last_month) }}
              empty="Nothing needs attention." />
            <div className="text-xs text-grayscale-1100">
              Checks: EC2 status checks and CPU, database status and CPU, ECS running tasks, load balancer targets and errors,
              Amplify builds, CloudWatch alarms, and anything billed while not in use.
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const AwsPanel: React.FC<{ target?: AdminTargetIn; initialTab?: string }> = ({ target, initialTab }) => {
  const start = TAB_PARAM[String(target?.tab || '').toLowerCase()] || (initialTab && TABS.includes(initialTab) ? initialTab : TABS[0]);
  const [tab, setTab] = useState(start);
  const [seen, setSeen] = useState<Record<string, boolean>>({ [start]: true });
  const pick = useCallback((t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); }, []);
  useEffect(() => {
    const t = TAB_PARAM[String(target?.tab || '').toLowerCase()];
    if (t) pick(t);
  }, [target?.nonce, target?.tab, pick]);
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick(x => x + 1), []);
  const body: Record<string, React.ReactNode> = {
    'Overview': <Overview tick={tick} bump={bump} open={pick} />,
    'Invoices': <Invoices tick={tick} bump={bump} />,
    'Costs': <Costs tick={tick} bump={bump} />,
    'Resources': <Resources tick={tick} bump={bump} />,
    'Health': <Health tick={tick} bump={bump} />,
    'API spend': <SpendPanel target={target && TAB_PARAM[String(target.tab || '').toLowerCase()] === 'API spend'
      ? ({ ...target, tab: target.sub || '' } as AdminTargetIn) : undefined} />,
  };
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={pick} />
      {TABS.filter(t => seen[t]).map(t => (
        <div key={t} className={t === tab ? '' : 'hidden'}>{body[t]}</div>
      ))}
    </div>
  );
};

export default AwsPanel;
